import axios from 'axios';
import { AssetPrice, HistoricalData } from './api';

/**
 * CoinGecko API服务
 * 免费API，无需API密钥
 * 支持加密货币价格和历史数据
 */
class CoinGeckoService {
  private client = axios.create({
    baseURL: 'https://api.coingecko.com/api/v3',
    timeout: 10000,
  });

  // 加密货币ID到名称的映射
  private idToName: Record<string, string> = {
    bitcoin: 'Bitcoin',
    btc: 'Bitcoin',
    ethereum: 'Ethereum',
    eth: 'Ethereum',
    binancecoin: 'BNB',
    bnb: 'BNB',
    solana: 'Solana',
    sol: 'Solana',
    cardano: 'Cardano',
    ada: 'Cardano',
    ripple: 'XRP',
    xrp: 'XRP',
    dogecoin: 'Dogecoin',
    doge: 'Dogecoin',
    polkadot: 'Polkadot',
    dot: 'Polkadot',
    polygon: 'Polygon',
    matic: 'Polygon',
    avalanche: 'Avalanche',
    'avalanche-2': 'Avalanche',
    avax: 'Avalanche',
    chainlink: 'Chainlink',
    link: 'Chainlink',
    uniswap: 'Uniswap',
    uni: 'Uniswap',
    litecoin: 'Litecoin',
    ltc: 'Litecoin',
    cosmos: 'Cosmos',
    atom: 'Cosmos',
  };

  // 符号到CoinGecko ID的映射
  private symbolToId: Record<string, string> = {
    btc: 'bitcoin',
    bitcoin: 'bitcoin',
    eth: 'ethereum',
    ethereum: 'ethereum',
    bnb: 'binancecoin',
    binancecoin: 'binancecoin',
    sol: 'solana',
    solana: 'solana',
    ada: 'cardano',
    cardano: 'cardano',
    xrp: 'ripple',
    ripple: 'ripple',
    doge: 'dogecoin',
    dogecoin: 'dogecoin',
    dot: 'polkadot',
    polkadot: 'polkadot',
    matic: 'polygon',
    polygon: 'polygon',
    avax: 'avalanche-2',
    avalanche: 'avalanche-2',
    'avalanche-2': 'avalanche-2',
    link: 'chainlink',
    chainlink: 'chainlink',
    uni: 'uniswap',
    uniswap: 'uniswap',
    ltc: 'litecoin',
    litecoin: 'litecoin',
    atom: 'cosmos',
    cosmos: 'cosmos',
  };

  /**
   * 获取加密货币价格
   */
  async getCryptoPrice(ids: string[]): Promise<AssetPrice[]> {
    try {
      // 转换ID为CoinGecko格式
      const coingeckoIds = ids.map(id => this.symbolToId[id.toLowerCase()] || id.toLowerCase());
      
      // CoinGecko API支持批量查询
      const response = await this.client.get('/simple/price', {
        params: {
          ids: coingeckoIds.join(','),
          vs_currencies: 'usd',
          include_24hr_change: true,
          include_24hr_vol: false,
          include_last_updated_at: true,
        },
      });

      const results: AssetPrice[] = [];
      const priceData = response.data;

      for (const id of ids) {
        const coingeckoId = this.symbolToId[id.toLowerCase()] || id.toLowerCase();
        const data = priceData[coingeckoId];

        if (data && data.usd) {
          const currentPrice = data.usd;
          const priceChange24h = data.usd_24h_change ? (currentPrice * data.usd_24h_change) / 100 : 0;
          const priceChangePercent24h = data.usd_24h_change || 0;
          const lastUpdated = data.last_updated_at 
            ? new Date(data.last_updated_at * 1000).toISOString()
            : new Date().toISOString();

          results.push({
            id: id.toLowerCase(),
            symbol: id.toUpperCase(),
            name: this.getAssetName(id),
            current_price: currentPrice,
            price_change_24h: priceChange24h,
            price_change_percentage_24h: priceChangePercent24h,
            last_updated: lastUpdated,
            source_url: this.client.defaults.baseURL || 'https://api.coingecko.com/api/v3',
          });
        }
      }

      return results;
    } catch (error) {
      console.error('CoinGecko获取加密货币价格失败:', error);
      throw error;
    }
  }

  /**
   * 获取历史数据
   */
  async getHistoricalData(id: string, days: number = 7): Promise<HistoricalData> {
    try {
      const coingeckoId = this.symbolToId[id.toLowerCase()] || id.toLowerCase();
      
      const response = await this.client.get(`/coins/${coingeckoId}/market_chart`, {
        params: {
          vs_currency: 'usd',
          days: days,
          interval: days <= 1 ? 'hourly' : 'daily',
        },
      });

      const prices: [number, number][] = (response.data.prices || []).map(([timestamp, price]: [number, number]) => [
        timestamp,
        price,
      ]);

      const market_caps: [number, number][] = (response.data.market_caps || []).map(([timestamp, cap]: [number, number]) => [
        timestamp,
        cap,
      ]);

      const total_volumes: [number, number][] = (response.data.total_volumes || []).map(([timestamp, volume]: [number, number]) => [
        timestamp,
        volume,
      ]);

      return {
        prices,
        market_caps,
        total_volumes,
      };
    } catch (error) {
      console.error('CoinGecko获取历史数据失败:', error);
      throw error;
    }
  }

  /**
   * 获取资产名称
   */
  private getAssetName(id: string): string {
    const lowerId = id.toLowerCase();
    return this.idToName[lowerId] || id.charAt(0).toUpperCase() + id.slice(1).toLowerCase();
  }
}

export const coingeckoService = new CoinGeckoService();
